import { SystemModeType } from "constants/enum";
import { Pin } from "interfaces/models/pin";
import { useCallback } from "react";
import { getCurrentViewer, getObjectGlobalPositionByDbId } from "utils/forge";
import {
  getLabelExtension,
  NormalDisplayItem,
} from "utils/forge/extensions/custom-label";
import { changeMaterialMultipleDbIds } from "utils/forge/forge3d";
import { useContextInspectionDetail } from "../context/inspectionDetail";
import {
  DETAIL_MODE,
  HighlightObject,
  TARGET_OBJECT_GROUP_DETAIL_COLOR,
} from "../context/inspectionDetailReducer";
import { Point } from "../context/InspectionAddPin/reducer";
import { generatePinCombineId } from "utils/forge/extensions/custom-label/utils";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { getDbIdByExternalId } from "utils/forge/viewerData";

interface Props {
  targetObjectColor?: string;
  pinObjectColor?: string;
}

const useForgeLabel = (props?: Props) => {
  const targetObjectColor =
    props?.targetObjectColor || TARGET_OBJECT_GROUP_DETAIL_COLOR;

  const { isLoadedExternalId } = useSelector(
    (state: RootState) => state.forgeViewer
  );

  const { mode, targetObjects, inspectionGroup } = useContextInspectionDetail();

  const clearPinLabels = useCallback(() => {
    const labelExtension = getLabelExtension();
    if (labelExtension) {
      labelExtension.clear();
      labelExtension.data = {};
    }
  }, []);

  const showPointLabels = useCallback(
    (points: Point[], offsetOrder: number = 0) => {
      const labelExtension = getLabelExtension();
      const displayItems: any = [];
      points.forEach((point, index) => {
        displayItems.push({
          id: point.id,
          position: point.position as any,
          title: offsetOrder + index + 1,
          displayOrder: index + offsetOrder,
        });
      });
      labelExtension?.showLabels({
        data: displayItems,
        mode: SystemModeType.Document,
      });
    },
    []
  );

  const showPinLabels = useCallback(
    (pins: Pin[]) => {
      const labelExtension = getLabelExtension();
      const inspectionGroupId = inspectionGroup?.id;

      clearPinLabels();
      const isAddPinExist =
        mode === DETAIL_MODE.ADD_PIN_EXIST && !!inspectionGroupId;

      // Helper function to create NormalDisplayItem
      const createDisplayItem = (
        pin: Pin,
        index: number
      ): NormalDisplayItem => ({
        id: pin.id,
        externalId: pin?.objects?.[0] || "",
        position: pin.position as any,
        title: undefined as any,
        status: pin.status,
        displayOrder: index,
        isAddPin:
          isAddPinExist &&
          !pin.pinGroups?.some((p) => p.pinGroupId === inspectionGroupId),
        itemSamePositions: [],
      });

      const itemList = pins.map(createDisplayItem);

      // Group pins by externalId to handle label combination
      const groupPinByPosition = itemList.reduce(
        (acc: Record<string, NormalDisplayItem[]>, pin) => {
          // if externalId is empty (object on space) then group by pinId
          const key = pin?.externalId || pin.id;
          if (acc[key]) {
            acc[key].push(pin);
          } else {
            acc[key] = [pin];
          }

          return acc;
        },
        {}
      );

      const combinedDisplayItems: NormalDisplayItem[] = [];

      Object.values(groupPinByPosition).forEach((items) => {
        if (items.length === 1) {
          combinedDisplayItems.push(...items);

          return;
        }

        const parentItem = structuredClone(items[0]);
        parentItem.id = generatePinCombineId(items.map((item) => item.id));
        parentItem.isCombine = true;
        parentItem.isAddPin = items.some((item) => item.isAddPin);
        parentItem.displayOrder = Math.min(
          ...items.map((i) => i.displayOrder ?? 0)
        );

        items.forEach((item) => {
          item.parentId = parentItem.id;
          parentItem.itemSamePositions?.push(item);
        });

        combinedDisplayItems.push(parentItem);
      });

      labelExtension?.showLabels({
        data: combinedDisplayItems,
        mode: SystemModeType.Document,
      });
    },
    [mode, inspectionGroup?.id, clearPinLabels]
  );

  const showObjectLabels = useCallback(
    (objects: HighlightObject[], offsetOrder: number = 0) => {
      const labelExtension = getLabelExtension();

      clearPinLabels();
      const displayItems: NormalDisplayItem[] = [];
      objects.forEach((obj, index) => {
        const position =
          obj?.position ?? getObjectGlobalPositionByDbId(obj.dbId);
        displayItems.push({
          id: obj.externalId,
          externalId: obj.externalId,
          position: position!,
          title: undefined as any,
          displayOrder: index + offsetOrder,
        });
      });
      labelExtension?.showLabels({
        data: displayItems,
        mode: SystemModeType.Document,
      });
    },
    [clearPinLabels]
  );

  const highlightTargetObjects = useCallback(() => {
    if (!isLoadedExternalId) {
      return;
    }
    const viewer = getCurrentViewer();
    if (!viewer?.model) {
      return;
    }

    const dbIds: number[] = [];
    targetObjects?.forEach((obj) => {
      const dbId = getDbIdByExternalId(obj.externalId);
      if (dbId) {
        viewer.show(dbId);
        dbIds.push(dbId);
      }
    });
    changeMaterialMultipleDbIds({ dbIds, color: targetObjectColor });
  }, [targetObjects, isLoadedExternalId, targetObjectColor]);

  return {
    showPointLabels,
    highlightTargetObjects,
    clearPinLabels,
    showObjectLabels,
    showPinLabels,
  };
};

export default useForgeLabel;
